import { nullishFilter } from '@caresend/utils';
import isEqual from 'lodash.isequal';
import { defineStore } from 'pinia';
import { ref, watch, computed } from 'vue';
import { useItineraryGeoSearch } from '../../useItineraryGeoSearch.js';
import { usePortalStore } from '../../../store/portal/index.js';
import { addAndMergeWithWaypoint, addWaypointAtIndex } from '../../../store/portal/set/index.js';
import { waypointStartTimes } from '../../../store/portal/set/fix.js';

const useListController = defineStore('useListController', () => {
    var _a;
    const portalStore = usePortalStore();
    const itineraryGeoSearch = useItineraryGeoSearch();
    const waypointIDs = ref((_a = portalStore.itinerary.value.waypoints.map(({ id }) => id)) !== null && _a !== void 0 ? _a : []);
    const setWaypointIDs = (newWaypointIDs) => {
        waypointIDs.value = newWaypointIDs;
    };
    watch([
        () => portalStore.itinerary.value.waypoints,
    ], () => {
        var _a;
        waypointIDs.value = (_a = portalStore.itinerary.value.waypoints.map(({ id }) => id)) !== null && _a !== void 0 ? _a : [];
    }, { deep: true });
    watch(waypointIDs, (newWaypointIDs) => {
        if (!portalStore.state.value.isReady)
            return;
        const reorderedWaypointIDs = newWaypointIDs
            .map((waypointID) => portalStore.waypoints.getRaw(waypointID))
            .filter(nullishFilter);
        if (isEqual(reorderedWaypointIDs, portalStore.itinerary.getters.currentWaypoints()))
            return;
        const reorderedWaypoints = reorderedWaypointIDs
            .map((waypoint) => portalStore.waypoints.getRaw(waypoint.id))
            .filter(nullishFilter)
            .map(({ id }) => ({ id }));
        portalStore.itinerary.set.waypoints(reorderedWaypoints, true);
        waypointStartTimes({ fix: true });
    });
    const getter = (draftWaypoint) => portalStore.draftWaypoints.getters.preview(draftWaypoint);
    const mappedDraftWaypoints = computed(() => itineraryGeoSearch.searchResults.draftWaypoints.map(getter));
    const previewWaypoints = ref(mappedDraftWaypoints.value);
    const usablePreviewWaypoints = computed(() => previewWaypoints.value.filter(({ draftWaypointID }) => !portalStore.itinerary.getters.draftWaypointIDs()[draftWaypointID]));
    watch(mappedDraftWaypoints, () => {
        mappedDraftWaypoints.value.forEach(({ waypoint, waypointActions }) => {
            if (!portalStore.waypoints.get(waypoint.id)) {
                portalStore.waypoints.set(waypoint);
            }
            waypointActions.forEach((wp) => {
                if (!portalStore.waypointActions.get(wp.id)) {
                    portalStore.waypointActions.set(wp);
                }
            });
        });
        previewWaypoints.value = mappedDraftWaypoints.value;
    }, { deep: true, immediate: true });
    const addWaypointToItinerary = ({ id, preview }) => {
        const { waypoint, waypointActions, draftWaypointID } = preview;
        const isDraftWaypointInItinerary = portalStore.itinerary.getters.draftWaypointIDs()[draftWaypointID];
        if (isDraftWaypointInItinerary)
            return;
        if (id) {
            addAndMergeWithWaypoint(waypoint, waypointActions, id);
        }
        else {
            addWaypointAtIndex(waypoint, waypointActions);
        }
    };
    const searchResultListResponse = (input) => {
        if (portalStore.itinerary.value.waypoints.length === 0) {
            addWaypointAtIndex(input.waypoint, input.waypointActions);
        }
    };
    const setWaypointIndex = (waypointID, index) => {
        let newIndex = index;
        if (newIndex > waypointIDs.value.length)
            newIndex = waypointIDs.value.length;
        if (newIndex < 0)
            return;
        const newWaypointIDs = waypointIDs.value.filter((id) => id !== waypointID);
        newWaypointIDs.splice(newIndex, 0, waypointID);
        waypointIDs.value = newWaypointIDs;
    };
    return {
        addWaypointToItinerary,
        previewWaypoints,
        searchResultListResponse,
        setWaypointIndex,
        usablePreviewWaypoints,
        waypointIDs,
        setWaypointIDs,
    };
});

export { useListController };
